import Breadcrumbs from "@mui/material/Breadcrumbs";
import { Container } from "@mui/material";
import EmptyState from "components/empty-state";
import Layout from "components/layout";
import Link from "components/link";
import { SubmitOnEnterKeyPress } from "components/submit-on-enter-key-press";
import TableBase from "components/table-base";
import TablePagination from "components/table-pagination";
import { ROUTES } from "constants/routes";
import { usePublicSpecialistLicensesAndStamps } from "hooks/api/use-public-specialist-licenses-and-stamps";
import { useDictionary } from "hooks/use-dictionary";
import { useUrlParams } from "hooks/use-url-params";
import { useEffect } from "react";
import { PublicSpecialistLicenseAndStampFilters } from "types/specialist-license-and-stamp";
import FiltersAlert from "./components/filters-alerts";
import FiltersBar from "./components/filters-bar";

const displayValue = (value: string | null | undefined) => value ?? "—";

const SpecialistLicensesPage = () => {
  const registry = usePublicSpecialistLicensesAndStamps();
  const { items, filters, setFilters, tableTop, ...pagination } = registry;
  const dict = useDictionary();
  const { filters: urlFilters } = useUrlParams();

  useEffect(() => {
    if (Object.keys(urlFilters).length > 0) {
      setFilters(urlFilters);
    }
  }, [urlFilters]);

  useEffect(() => {
    if (pagination.page > pagination.pages) {
      pagination.onPage(1);
    }
  }, [pagination]);

  const handleFilterSubmit = (newFilters: PublicSpecialistLicenseAndStampFilters) => {
    pagination.onPage(1);
    setFilters(newFilters);
  };

  const handleFilterClear = () => {
    pagination.onPage(1);
    setFilters({});
  };

  return (
    <Layout>
      <Container maxWidth="xl">
        <div className="space-y-6 py-10">
          <Breadcrumbs>
            <Link to={ROUTES.home}>{dict.common.breadcrumbsRoot}</Link>
            <div>{dict.specialistLicenses.title}</div>
          </Breadcrumbs>
          <div className="text-2xl font-bold">{dict.specialistLicenses.title}</div>
          <SubmitOnEnterKeyPress>
            <FiltersBar onSubmit={handleFilterSubmit} onClear={handleFilterClear} />
          </SubmitOnEnterKeyPress>
          <FiltersAlert filters={filters as PublicSpecialistLicenseAndStampFilters} />
          <div>
            {tableTop}
            {items.length === 0 ? (
              <div className="flex flex-col items-center bg-white border py-8">
                <EmptyState title={dict.specialistLicenses.emptyState.title} text={dict.specialistLicenses.emptyState.description} />
              </div>
            ) : (
              <div className="overflow-auto border-r">
                <TableBase>
                  <thead>
                    <tr>
                      <th>{dict.specialistLicenses.stampNumber}</th>
                      <th>{dict.specialistLicenses.stampType}</th>
                      <th>{dict.specialistLicenses.firstName}</th>
                      <th>{dict.specialistLicenses.lastName}</th>
                      <th>{dict.specialistLicenses.licenseNumber}</th>
                      <th>{dict.specialistLicenses.qualification}</th>
                      <th>{dict.specialistLicenses.licenseIssueDate}</th>
                      <th>{dict.specialistLicenses.licenseStatus}</th>
                      <th>{dict.specialistLicenses.orderDateAndNumber}</th>
                      <th>{dict.specialistLicenses.supervisionDate}</th>
                      <th>{dict.specialistLicenses.supervisionOrderNumber}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, index) => {
                      const qualification = item.license?.professionalQualificationName ?? item.stamp?.professionalQualificationName;
                      const order = item.license?.orderDate && item.license.orderNumber
                        ? `${item.license.orderDate} ${item.license.orderNumber}`
                        : null;

                      return (
                        <tr key={`${item.stamp?.number ?? ""}-${item.license?.number ?? ""}-${index}`}>
                          <td>{displayValue(item.stamp?.number)}</td>
                          <td>{displayValue(item.stamp?.type)}</td>
                          <td>{item.firstName}</td>
                          <td>{item.lastName}</td>
                          <td>{displayValue(item.license?.number)}</td>
                          <td>{displayValue(qualification)}</td>
                          <td>{displayValue(item.license?.issueDate)}</td>
                          <td>{item.license?.status ? dict.specialistUserLicenseStatus[item.license.status] : "—"}</td>
                          <td>{displayValue(order)}</td>
                          <td>{displayValue(item.license?.supervisionDate)}</td>
                          <td>{displayValue(item.license?.supervisionOrderNumber)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </TableBase>
              </div>
            )}
            <TablePagination className="bg-white rounded-b border-x border-b px-5 py-2" {...pagination} />
          </div>
        </div>
      </Container>
    </Layout>
  );
};

export default SpecialistLicensesPage;
